import { z } from 'zod';
import { FinanceInvoiceStatusSchema } from './admin-finance';
import { BiddingModeKindSchema, DepositMultiplierSchema } from './bidding-mode';
import { BidStatus } from './car-enums';
/**
 * The admin «Угоди» queue (admin deals & deposits — phase 1, SC-11).
 *
 * A "deal" on that screen is NOT a stored document: it is a READ MODEL over
 * what already exists — the bid (`status` + `relayStatus`), the post-win
 * `deals` ladder (`deal.ts`) and the invoice. {@link resolveDealStage} is the
 * one pure mapping from those three to the row's stage and its single next
 * action. Stage WRITES go through the machines that already exist (relay
 * endpoints, `DealsService.transition`), never through this module.
 */
/** Where one deal stands on the admin screen. */
export const DealStageSchema = z.enum([
    'relay', // client bid captured/queued — waiting to be mirrored upstream
    'leading', // mirrored upstream, tender still open
    'won_uninvoiced', // won, no invoice yet
    'awaiting_payment', // invoiced, nothing received
    'payment_checking', // client said «Я сплатив» — the operator must check
    'payment_overdue', // invoiced and past due
    'shipping', // paid, somewhere on the delivery ladder
    'handed_over', // delivered and closed
    'outbid', // outbid on our site — nothing for the admin to do
    'lost', // not won / relay ended without a win
    'forfeited', // client refused to pay — deposit withheld, invoice cancelled (decision a)
    'voided', // the win was re-awarded to another bid — the deal is annulled (BUG-07)
    'in_ledger', // an ADMIN's own win, already a card in the local ledger (derived, never stored)
]);
export const DEAL_STAGES = DealStageSchema.options;
/**
 * The screen's tabs — the artboard's `dealTabs` plus «Анульовано» (BUG-07)
 * and «В обліку» (local-ledger-admin-bridge): Усі · Ретрансляція · Активні
 * ставки · Без рахунку · Оплата · Доставка · Завершені · В обліку ·
 * Анульовано. «Анульовано» is last: it is an archive of wins that were
 * re-awarded, never a step of the work.
 */
export const DEAL_QUEUE_TABS = [
    'all',
    'active',
    'finished',
    'archive',
    'relay',
    'leading',
    // Owner, 2026-10-25: a relay/leading row whose REAL auction has closed — the
    // operator owes it «Виграно / Не виграно». Derived from the clock, see
    // {@link dealQueueTabOf}; never a stage of its own.
    'awaiting_outcome',
    'won_uninvoiced',
    'payment',
    'shipping',
    'done',
    'in_ledger',
    'voided',
];
export const DealQueueTabSchema = z.enum(DEAL_QUEUE_TABS);
/**
 * The tab a stage is listed under (never `all` — every listed row is in it).
 * `payment` folds the three payment stages, `done` folds `handed_over`,
 * `lost` and `forfeited` (the client refused to pay — nothing is left to do).
 * `voided` is its own tab: a win that was re-awarded is neither a sale nor a
 * loss, and mixing it into «Завершені» hid it (BUG-07). `outbid` is NOT in the
 * queue: there is nothing for the admin to do and the amount is already free
 * again (rule 03). `in_ledger` is its own tab too: an admin's own win handed
 * to the local ledger is neither invoiced nor lost — the platform's part ends
 * there.
 */
export function dealTabOf(stage) {
    switch (stage) {
        case 'relay':
        case 'leading':
        case 'won_uninvoiced':
        case 'shipping':
        case 'voided':
        case 'in_ledger':
            return stage;
        case 'awaiting_payment':
        case 'payment_checking':
        case 'payment_overdue':
            return 'payment';
        case 'handed_over':
        case 'lost':
        case 'forfeited':
            return 'done';
        case 'outbid':
            return null;
    }
}
/**
 * The row's narrow tab with the clock applied (owner, 2026-10-25): a lot still
 * waiting to be mirrored (`relay`) or mirrored and leading (`leading`) whose
 * REAL auction has already closed is no longer «active» — the operator owes it
 * «Виграно / Не виграно», so it is listed under `awaiting_outcome`. A lot with
 * no known end stays where its stage puts it.
 */
export function dealQueueTabOf(stage, auctionEndAt, now) {
    const tab = dealTabOf(stage);
    if (tab !== 'relay' && tab !== 'leading')
        return tab;
    const end = auctionEndAt?.getTime();
    return end !== undefined && Number.isFinite(end) && end <= now.getTime()
        ? 'awaiting_outcome'
        : tab;
}
/**
 * The screen's three tabs. Owner, 2026-10-12: «Активні» — lots whose real
 * auction is still being played (relayed or waiting to be). Owner, 2026-10-25:
 * «Завершені» — what still waits on the operator after the auction: the
 * outcome to record, a win to invoice or to be paid; «Архів» — a paid car on
 * its way (2026-10-26) and every lot with a final status (handed over, lost,
 * refused, annulled, in the ledger). The narrow tabs become the «Етап» filter inside
 * each group; `all` / the groups never come out of {@link dealQueueTabOf}, they
 * are unions of what does.
 */
export const DEAL_QUEUE_GROUPS = {
    active: ['relay', 'leading'],
    finished: ['awaiting_outcome', 'won_uninvoiced', 'payment'],
    // Owner, 2026-10-26: a PAID car (on delivery) is archived too — «Завершені»
    // is only what still waits on the outcome or the money.
    archive: ['shipping', 'done', 'in_ledger', 'voided'],
};
/** The group a narrow tab belongs to; a group is its own group; `all` has none. */
export function dealGroupOf(tab) {
    if (tab === 'active' || tab === 'finished' || tab === 'archive')
        return tab;
    if (tab === 'all')
        return null;
    const groups = Object.keys(DEAL_QUEUE_GROUPS);
    return (groups.find((group) => DEAL_QUEUE_GROUPS[group].includes(tab)) ?? null);
}
/** True when a row listed under `rowTab` belongs on the screen's `tab`. */
export function dealTabMatches(rowTab, tab) {
    return tab === 'all' || rowTab === tab || dealGroupOf(rowTab) === tab;
}
/** The ONE next action a row offers. */
export const DealNextActionSchema = z.enum([
    'mirror',
    'record_outcome',
    'issue_invoice',
    'confirm_payment',
    'remind_payment',
    'advance_shipping',
    'hand_over',
    // An admin's own win (local-ledger-admin-bridge): «Додати до обліку» creates
    // the card through `POST /api/admin/bids/:bidId/local-vehicle`; «Відкрити в
    // обліку» opens it on local.cars-finders.com.
    'add_to_ledger',
    'open_in_ledger',
]);
export const DEAL_NEXT_ACTIONS = DealNextActionSchema.options;
/**
 * Deal-ladder state → delivery step (owner decision g): Доставка 1 =
 * `paid`|`export_docs`, 2 = `in_transit`, 3 = `customs`, 4 = `delivered`
 * (ready for handover). `closed` is «Видано», off the step bar.
 */
export const SHIP_STEP_BY_STATE = {
    paid: 0,
    export_docs: 0,
    in_transit: 1,
    customs: 2,
    delivered: 3,
};
/**
 * «Наступний етап доставки» from step k advances the deal to
 * `SHIP_STEP_TARGET_STATE[k]` (walking the ladder hop by hop — from `paid` the
 * walk passes `export_docs`). From step 3 the target is `closed` (handover).
 */
export const SHIP_STEP_TARGET_STATE = ['in_transit', 'customs', 'delivered', 'closed'];
/** Relay outcomes that end a bid without a win. */
const RELAY_LOST_STATES = ['lost', 'cancelled', 'expired', 'upstream_rejected'];
function isShippingState(state) {
    return Object.prototype.hasOwnProperty.call(SHIP_STEP_BY_STATE, state);
}
/**
 * A win with nothing invoiced yet. A client's waits for its invoice; an
 * ADMIN's own win is never invoiced — its next step is the local ledger:
 * «Додати до обліку» until the card exists, then `in_ledger` with «Відкрити в
 * обліку».
 */
function wonStage(ledger) {
    if (ledger.bidderIsAdmin) {
        return ledger.inLedger
            ? { stage: 'in_ledger', shipStep: null, nextAction: 'open_in_ledger' }
            : { stage: 'won_uninvoiced', shipStep: null, nextAction: 'add_to_ledger' };
    }
    return { stage: 'won_uninvoiced', shipStep: null, nextAction: 'issue_invoice' };
}
function stageOfDeal(dealState, invoiceStatus, ledger) {
    // BUG-07: listed under «Анульовано» instead of vanishing from the queue.
    if (dealState === 'voided')
        return { stage: 'voided', shipStep: null, nextAction: null };
    if (dealState === 'won')
        return wonStage(ledger);
    if (dealState === 'invoice_issued' || dealState === 'deposit_applied') {
        if (invoiceStatus === 'checking') {
            return { stage: 'payment_checking', shipStep: null, nextAction: 'confirm_payment' };
        }
        if (invoiceStatus === 'overdue') {
            return { stage: 'payment_overdue', shipStep: null, nextAction: 'remind_payment' };
        }
        return { stage: 'awaiting_payment', shipStep: null, nextAction: 'confirm_payment' };
    }
    if (isShippingState(dealState)) {
        const shipStep = SHIP_STEP_BY_STATE[dealState];
        return {
            stage: 'shipping',
            shipStep,
            nextAction: shipStep === 3 ? 'hand_over' : 'advance_shipping',
        };
    }
    if (dealState === 'closed') {
        return { stage: 'handed_over', shipStep: null, nextAction: null };
    }
    if (dealState === 'forfeited') {
        return { stage: 'forfeited', shipStep: null, nextAction: null };
    }
    // A new ladder state must be mapped above, not shown as «Видано» by default.
    const unmapped = dealState;
    throw new Error(`unmapped deal state: ${String(unmapped)}`);
}
/**
 * THE stage of one deal row. Pure. Every input resolves to a stage — a
 * `voided` deal (its win was re-awarded) included, since BUG-07 lists it
 * under «Анульовано»; whether a stage is LISTED is {@link dealTabOf}'s call
 * (`outbid` is not). The `| null` return is kept for callers that still
 * guard it.
 *
 * With a deal the ladder decides (the invoice status refines the two invoiced
 * rungs). Without one the bid decides, in this precedence:
 *  1. `Won` (legacy win with no deal) or relay `won` → `won_uninvoiced`;
 *  2. `Outbid` → `outbid`;
 *  3. `Not Won`, or relay `lost|cancelled|expired|upstream_rejected` → `lost`;
 *  4. `Under Review` + relay `relayed` → `leading` (record the outcome);
 *  5. `Under Review` + relay `captured|queued|null` → `relay` (mirror it).
 *
 * A win not yet invoiced (deal `won`, or rule 1) of an ADMIN bidder
 * (`bidderIsAdmin`) is the owners' own purchase: `won_uninvoiced` +
 * `add_to_ledger` until a local-ledger card names the bid (`inLedger`), then
 * `in_ledger` + `open_in_ledger`. A client's keeps `issue_invoice`.
 */
export function resolveDealStage(input) {
    if (input.dealState !== null)
        return stageOfDeal(input.dealState, input.invoiceStatus, input);
    const { bidStatus, relayStatus } = input;
    if (bidStatus === BidStatus.Won || relayStatus === 'won')
        return wonStage(input);
    if (bidStatus === BidStatus.Outbid) {
        return { stage: 'outbid', shipStep: null, nextAction: null };
    }
    if (bidStatus === BidStatus.Cancelled ||
        (relayStatus !== null && RELAY_LOST_STATES.includes(relayStatus))) {
        return { stage: 'lost', shipStep: null, nextAction: null };
    }
    if (relayStatus === 'relayed') {
        return { stage: 'leading', shipStep: null, nextAction: 'record_outcome' };
    }
    return { stage: 'relay', shipStep: null, nextAction: 'mirror' };
}
// ── The queue wire shapes (admin deals & deposits — phase 7) ─────────────────
/** Page size bounds of `GET /api/admin/deals/queue`. */
export const DEAL_QUEUE_DEFAULT_LIMIT = 50;
export const DEAL_QUEUE_MAX_LIMIT = 100;
/**
 * How long a lost row stays in «Завершені». Lost bids are listed for the last
 * 30 days only, so the tab cannot grow without bound.
 */
export const DEAL_QUEUE_LOST_WINDOW_DAYS = 30;
/**
 * The queue's orders (owner, 2026-10-12: «сортування так як у клієнта»).
 * `queue` is the work order — SLA-overdue first, then the longest-waiting;
 * `endSoon` puts live lots first, soonest real auction close first, then
 * finished ones, most recent first; `bidHigh` / `bidLow` by the client's bid.
 * Ties fall back to `bidId`, so every order is total and a cursor is stable.
 */
export const DEAL_QUEUE_SORTS = ['queue', 'endSoon', 'bidHigh', 'bidLow'];
export const DealQueueSortSchema = z.enum(DEAL_QUEUE_SORTS);
/** `GET /api/admin/deals/queue` query. `q` searches client name/email, car title and lot number. */
export const DealQueueQuerySchema = z.object({
    tab: DealQueueTabSchema.default('all'),
    sort: DealQueueSortSchema.default('queue'),
    /** One client's lots only (`users._id`) — the client page's «Ставки» tab. */
    userId: z
        .string()
        .regex(/^[a-f0-9]{24}$/i)
        .optional(),
    q: z.string().trim().max(80).optional(),
    /**
     * One auction's lots only — the row's `auctionHouse` as the «Аукціон» filter
     * lists it (owner, 2026-10-13: «фільтр по аукціонах»).
     */
    auction: z.string().trim().min(1).max(80).optional(),
    /** Opaque — the `nextCursor` of the previous page, never built by a client. */
    cursor: z.string().max(512).optional(),
    limit: z.coerce.number().int().min(1).max(DEAL_QUEUE_MAX_LIMIT).default(DEAL_QUEUE_DEFAULT_LIMIT),
});
/** The client's bidding mode as the row shows it; null when the client was never backfilled. */
export const DealQueueClientModeSchema = z.object({
    mode: BiddingModeKindSchema,
    multiplier: DepositMultiplierSchema.nullable(),
    bidLimit: z.number().nullable(),
});
/** The latest CAR invoice of the row's deal. `dueChf` is what is still outstanding. */
export const DealQueueInvoiceSchema = z.object({
    no: z.string(),
    dueChf: z.number(),
    /**
     * Already received against the invoice (settlements), CHF. The forfeit
     * dialog names it: a refusal retains it (owner, 2026-09-28). Defaults to 0
     * for a reader of an older producer.
     */
    paidChf: z.number().nonnegative().default(0),
    dueAt: z.coerce.date(),
    /** DISPLAY status (`deriveInvoiceStatus`), so `overdue` is already derived. */
    status: FinanceInvoiceStatusSchema,
});
/**
 * ONE row of the «Угоди» queue: one client lot. `stage`, `shipStep` and
 * `nextAction` are {@link resolveDealStage}'s answer, never re-derived by the
 * reader. `dealId` is null until the bid is won.
 */
export const DealQueueRowSchema = z.object({
    bidId: z.string(),
    dealId: z.string().nullable(),
    carId: z.string(),
    lotNumber: z.string().nullable(),
    auctionHouse: z.string().nullable(),
    carTitle: z.string(),
    imageUrl: z.string().nullable(),
    /**
     * When the lot's real auction closes (no customer offset), or null when the
     * lot carries no end. Optional on the wire for a reader of an older producer.
     */
    auctionEndAt: z.coerce.date().nullish(),
    clientId: z.string(),
    clientName: z.string(),
    clientMode: DealQueueClientModeSchema.nullable(),
    bidChf: z.number(),
    stage: DealStageSchema,
    shipStep: z.number().int().min(0).max(3).nullable(),
    nextAction: DealNextActionSchema.nullable(),
    invoice: DealQueueInvoiceSchema.nullable(),
    /** When the row entered its current stage — the deal's `stateEnteredAt`, else the bid's last write. */
    stageEnteredAt: z.coerce.date(),
    slaOverdue: z.boolean(),
    /**
     * The local-ledger card this bid became (`local_vehicles.sourceBidId`) — what
     * «Відкрити в обліку» opens. Null/absent when there is none (or an older producer).
     */
    localVehicleId: z.string().nullish(),
    /** The bidder holds the `admin` role (the owners' own bid). Absent on an older producer. */
    bidderIsAdmin: z.boolean().optional(),
    /**
     * The bid page's «Оброблено» mark (`bids.processedOnAuction`): the operator
     * placed the bid on the real auction. The `relay` row shows it instead of a
     * «Віддзеркалити» button (owner, 2026-10-25). Absent on an older producer.
     */
    processedOnAuction: z.boolean().optional(),
});
/**
 * The page plus the per-tab counters. Counters are over the whole source set
 * with `q` applied and `tab` ignored, so every tab badge is visible at once.
 */
/** One client of the queue's «Клієнт» filter: who has lots, and how many. */
export const DealQueueClientSchema = z.object({
    id: z.string(),
    name: z.string(),
    count: z.number().int().nonnegative(),
});
/** One auction of the queue's «Аукціон» filter: where lots are played, and how many. */
export const DealQueueAuctionSchema = z.object({
    name: z.string(),
    count: z.number().int().nonnegative(),
});
export const DealQueueResponseSchema = z.object({
    rows: z.array(DealQueueRowSchema),
    counts: z.record(DealQueueTabSchema, z.number().int().nonnegative()),
    nextCursor: z.string().nullable(),
    /**
     * Every client with a lot in the source set (search applied; tab and the
     * client filter itself ignored, so picking one never empties the list),
     * by name. Defaults to [] for a reader of an older producer.
     */
    clients: z.array(DealQueueClientSchema).default([]),
    /**
     * Every auction with a lot in the source set — same scope rule as `clients`
     * (the auction filter itself ignored), by name. [] for an older producer.
     */
    auctions: z.array(DealQueueAuctionSchema).default([]),
});
/**
 * `POST /api/admin/deals/:dealId/advance-shipping` body. `fromShipStep` is the
 * step the operator SAW: when set and the deal has moved on meanwhile, the
 * route answers 409 `STALE_SHIP_STEP` instead of advancing a second time (a
 * double click, or two operators on one row).
 */
export const DealAdvanceShippingBodySchema = z
    .object({ fromShipStep: z.number().int().min(0).max(3).optional() })
    .strict();
/** 409 codes of the two delivery routes. */
export const DEAL_DELIVERY_ERROR_CODES = {
    NOT_SHIPPING: 'NOT_SHIPPING',
    NOT_DELIVERED: 'NOT_DELIVERED',
    STALE_SHIP_STEP: 'STALE_SHIP_STEP',
};
//# sourceMappingURL=deal-stage.js.map